import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  clearSelectedGroups,
  isAlarmLoading,
  selectActiveAlarms,
  selectTypes,
  setAlarmCustomText,
  setSelectedGroupType,
} from './AlarmSlice';
import { fetchAllActiveAlarms, getEmergencyTypesAction } from './AlarmSlice/actionCreators';
import type { ActiveAlarm, EmergencyType } from './AlarmSlice/types';
import NoAlarms from '../../assets/images/box-open.svg';
import AlarmType from '../../components/AlarmItem/AlarmType';
import { Page } from '../../components/Page/Page';
import { ReactComponent as Arrow } from '../../assets/icons/chevron-up.svg';
import { selectGroups } from '../GroupsList/groupsSlice';
import type {
  AlarmFilterType,
  AlarmFilterTypeObject,
} from '../../components/AlarmFilter/AlarmFilter';
import { AlarmFilter } from '../../components/AlarmFilter/AlarmFilter';
import {
  LocalStorageFiltersKeys,
  checkIfFiltersApplied,
  useLocalStorageFilters,
} from '../../utils/customHooks/useLocalStorageFilters';
import { TitledAlarmTypeGroup } from '../../components/AlarmItem/TitledAlarmTypeGroup';
import { filterAlarmsByAccounts, getUserAlarmList, handleFiltersChange } from './helpers';
import { parseSentTime } from '../../utils/parseSentTime';
import { generateInitialSelectedData } from '../../components/DocumentFilter/helpers';
import {
  AlarmWrapper,
  NoAlarmWrapper,
  SAlertBanner,
  SPage,
  ScrollMoreBottom,
  ScrollMoreTop,
  TOP_PADDING_UNDER_FILTER_ALERT_BANNER,
  ScrollListContainer,
} from './Alarm.styles';
import Loader from '../../components/Loader/Loader';
import { useTopBottomScrollLimit } from '../../utils/customHooks/useTopBottomScrollLimit';
import type { AlertBannerRef } from '../../components/AlertBanner/AlertBanner';
import { getAppLanguage } from '../../i18n';
import { OngoingBlockingAlarmNotice } from './OngoingBlockingAlarmNotice';

export type AlarmsAccountTitle = {
  id: number;
  name: string;
  alarms: EmergencyType[];
};

type IAlarmProps = {
  filterButtonProps: {
    setFilterOpen: Dispatch<SetStateAction<boolean>>;
    filterOpen: boolean;
    renderFilterButton: boolean;
    setIsFilterApplied: Dispatch<SetStateAction<boolean>>;
    setRenderFilterButton: Dispatch<SetStateAction<boolean>>;
  };
};
const defaultFilters: AlarmFilterTypeObject = { selectedIds: 'SELECTED_ALL' };

function Alarm(props: IAlarmProps) {
  const {
    filterOpen,
    setFilterOpen,
    setIsFilterApplied,
    setRenderFilterButton,
    renderFilterButton,
  } = props.filterButtonProps;
  const dispatch = useAppDispatch();

  const alarmTypes = useAppSelector(selectTypes);
  const isLoading = useAppSelector(isAlarmLoading);
  const groups = useAppSelector(selectGroups);

  const [filters, setFilters] = useLocalStorageFilters<AlarmFilterTypeObject>(
    LocalStorageFiltersKeys.alarmsLis,
    { ...defaultFilters }
  );

  const isFilterApplied = checkIfFiltersApplied({
    defaultFilters: defaultFilters,
    filters,
  });

  const onFilter = (_filters: AlarmFilterType) => {
    if (_filters === 'UNSELECTED_ALL') {
      setFilters({ selectedIds: 'SELECTED_ALL' });
    } else {
      setFilters({ selectedIds: _filters });
    }
    setFilterOpen(false);
  };

  const alertRef = useRef<AlertBannerRef>(null);

  useLayoutEffect(() => {
    setIsFilterApplied(isFilterApplied);
  }, [isFilterApplied]);

  const { accountTitlesList, alarmList } = useMemo(
    () => getUserAlarmList(groups, alarmTypes),
    [groups, alarmTypes]
  );

  const currentLang = getAppLanguage();
  const activeAlarms = useAppSelector(selectActiveAlarms);

  const { hasActiveBlockingAlarm, blockingAlarmsList, isSectionDisabled } = useBlockingAlarms(
    activeAlarms,
    alarmTypes,
    currentLang
  );

  useEffect(() => {
    const shouldShowFilterButton = accountTitlesList.length > 1 && !hasActiveBlockingAlarm;
    if (shouldShowFilterButton !== renderFilterButton) {
      setRenderFilterButton(shouldShowFilterButton);
    }
  }, [accountTitlesList.length, hasActiveBlockingAlarm]);

  // we have to keep the useEffect in order for it to run when the alertRef.current to be defined
  useEffect(() => {
    handleFiltersChange(isFilterApplied, filters, alertRef);
  }, [isFilterApplied, filters.selectedIds, alertRef.current]);

  const { alarmRef, bottomArrowRef, checkScroll, handleScroll, topArrowRef, limit } =
    useTopBottomScrollLimit(alarmList);

  useEffect(() => {
    dispatch(getEmergencyTypesAction());
    dispatch(fetchAllActiveAlarms());
    // Clear selected groups and the type in store
    dispatch(clearSelectedGroups());
    dispatch(setSelectedGroupType([]));
    dispatch(setAlarmCustomText(''));
  }, [dispatch]);

  if (isLoading) {
    return <Loader />;
  }

  if (hasActiveBlockingAlarm) {
    const sheetSections = accountTitlesList.filter(item => !isSectionDisabled(item.id));
    const canTriggerOtherAlarms = sheetSections.length > 0;
    return (
      <OngoingBlockingAlarmNotice
        alarms={blockingAlarmsList}
        primaryColor={blockingAlarmsList[0]?.color}
        canTriggerOtherAlarms={canTriggerOtherAlarms}
        sheetSections={sheetSections}
        currentLang={currentLang}
      />
    );
  }

  if (alarmList.length === 0) {
    return (
      <Page>
        <NoAlarmWrapper>
          <img src={NoAlarms} alt="" />
          <p>You don’t have any alarms</p>
        </NoAlarmWrapper>
      </Page>
    );
  }

  return (
    <SPage>
      <ScrollMoreTop role="scrollMoreTop" onClick={() => handleScroll(true)} ref={topArrowRef}>
        <Arrow role="arrowUpIcon" />
      </ScrollMoreTop>
      <SAlertBanner
        preset="fixed"
        ref={alertRef}
        placeHolderHeight={TOP_PADDING_UNDER_FILTER_ALERT_BANNER}
        alertTopToValue={16}
        onClick={() => {
          setFilterOpen(true);
        }}
      />
      <AlarmWrapper
        ref={alarmRef}
        onScroll={checkScroll}
        role="alarmContainer"
        haveAlarmTitles={accountTitlesList.length > 2}
        $isFilterApplied={isFilterApplied}
      >
        <AlarmFilter
          defaultFilters={defaultFilters.selectedIds}
          data={accountTitlesList}
          isOpen={filterOpen}
          setIsOpen={setFilterOpen}
          onFilter={onFilter}
          initialSelectedData={generateInitialSelectedData(filters.selectedIds, accountTitlesList)}
        />

        <ScrollListContainer>
          {accountTitlesList.length < 2 ? (
            <>
              {alarmList.slice(0, limit).map((item, key) => (
                <AlarmType key={key} alarmTypeItem={item} currentLang={currentLang} />
              ))}
            </>
          ) : (
            filterAlarmsByAccounts(accountTitlesList, filters).map(item => (
              <TitledAlarmTypeGroup
                key={`TitledAlarmTypeGroup-${item.id}`}
                titledAlarmsGroup={item}
                currentLang={currentLang}
              />
            ))
          )}
        </ScrollListContainer>
      </AlarmWrapper>
      <ScrollMoreBottom
        role="scrollMoreBottom"
        onClick={() => handleScroll(false)}
        ref={bottomArrowRef}
      >
        <Arrow role="arrowDownIcon" />
      </ScrollMoreBottom>
    </SPage>
  );
}

export default Alarm;

const useBlockingAlarms = (
  activeAlarms: ActiveAlarm[],
  emergencyTypes: EmergencyType[],
  language: string
) => {
  const blockingAlarms = activeAlarms.filter(alarm => alarm.isBlockingOtherAlarms);
  const blockingAlarmsWithEmergencies = blockingAlarms.map(blockingAlarm => ({
    blockingAlarm,
    emergencyType: emergencyTypes.find(
      emergencyType => emergencyType.ID === blockingAlarm.emergencyTypeId
    ),
    organizationId: blockingAlarm.triggeringOrganization?.id,
    organizationName: blockingAlarm.triggeringOrganization?.name,
  }));

  const hasActiveBlockingAlarm = blockingAlarms.length > 0;

  const isSectionDisabled = (sectionId: number) =>
    blockingAlarmsWithEmergencies.some(({ organizationId }) => organizationId === sectionId);

  const sortedBlockingAlarmsWithEmergencies = blockingAlarmsWithEmergencies
    .slice()
    .sort(compareBySentDescending);

  const blockingAlarmsList = sortedBlockingAlarmsWithEmergencies.map(a => ({
    alarmId: a.blockingAlarm.id,
    alarmSentAt: a.blockingAlarm.sent,
    organizationName: a.organizationName ?? null,
    icon: a.emergencyType?.Icon ?? undefined,
    color: a.emergencyType?.Color ?? undefined,
    name:
      a.emergencyType?.Names?.find(({ Language }) => Language === language)?.Name ??
      a.blockingAlarm.emergencyTypeName,
  }));

  return {
    hasActiveBlockingAlarm,
    blockingAlarmsList,
    isSectionDisabled,
  };
};

const compareBySentDescending = <AlarmData extends { blockingAlarm: { sent: string | null } }>(
  a: AlarmData,
  b: AlarmData
) => parseSentTime(b.blockingAlarm.sent) - parseSentTime(a.blockingAlarm.sent);
